import React, { useMemo, useState } from 'react';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import DataDiary, { DiaryEvent } from '../../../../components/charts/DataDiary';
import styled from 'styled-components/native';
import { getAllEventOccurrences } from '../../helpers/ParticipantEventHelpers';
import { differenceInDays } from 'date-fns';
import { H3 } from '../../../../components/Typography';
import Card from '../../../../components/Card';
import IconButton from '../../../../components/IconButton';
import { Octicons } from '@expo/vector-icons';
import WebViewModal from '../../../modals/WebViewModal';
import elementIDs from '../../../../../elementIDs';

const StyledCard = styled(Card)`
  width: 100%;
  padding: 10px 16px 16px 16px;
  overflow: hidden;
`;

const StyledH3 = styled(H3)`
  margin-top: 10px;
  margin-left: 4px;
`;

const StyledTitleContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

export interface DataDiaryCardProps {
  chartTitle?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
}

// filter function to remove removed events due to custom days
function filterDiaryEvents(events: DiaryEvent[]): DiaryEvent[] {
  const identifierCount = new Map<string, number>();

  // Count all eventIdentifiers across ALL DiaryEvent objects
  for (const diary of events) {
    for (const event of diary.events) {
      if (!event.eventIdentifier) continue;

      const { intendedStartDate, studyTemplateEventId, repeatCount } =
        event.eventIdentifier;

      const key = `${intendedStartDate}-${studyTemplateEventId}-${repeatCount}`;
      identifierCount.set(key, (identifierCount.get(key) ?? 0) + 1);
    }
  }

  // remove events that are the same
  // events that are the same are considered removed as setting a custom day above a
  // reccurring event is considered removed
  return (
    events
      .map(diary => {
        const filteredEvents = diary.events.filter(event => {
          if (!event.eventIdentifier) return true;

          const { intendedStartDate, studyTemplateEventId, repeatCount } =
            event.eventIdentifier;

          const key = `${intendedStartDate}-${studyTemplateEventId}-${repeatCount}`;
          return identifierCount.get(key) === 1;
        });

        return {
          ...diary,
          events: filteredEvents
        };
      })
      // remove empty DiaryEvent objects
      .filter(diary => diary.events.length > 0)
  );
}

const DataDiaryChartCard: React.FC<
  ScreenComponentProps<DataDiaryCardProps>
> = props => {
  const { studyParticipant, chartTitle, hasMoreInfo, infoTitle, infoHtml } =
    props;
  const study = studyParticipant?.study;
  const participantEvents = studyParticipant?.participantEvents;
  const studyDuration =
    studyParticipant?.studyLength || study?.studyLength || 0;
  // make a copy of start date to prevent mutation
  const startDate = studyParticipant?.startDate
    ? new Date(studyParticipant.startDate).setHours(0, 0, 0)
    : undefined;
  const currentDay = startDate
    ? differenceInDays(new Date(), startDate) + 1
    : undefined;

  const diaryRecord = useMemo(() => {
    if (participantEvents) {
      let events: DiaryEvent[] = [];
      for (const participantEvent of participantEvents) {
        const { all, expired } = getAllEventOccurrences(
          participantEvent,
          studyParticipant,
          { sort: 'ASC', maxFutureOccurrences: 100 }
        );

        const allEvents = [...all, ...expired];
        if (startDate) {
          events.push({
            name: participantEvent.name,
            events: allEvents.map(event => {
              return {
                day: differenceInDays(event.intendedStartDate, startDate) + 1,
                done: event.participantEventLog?.completedDate
                  ? true
                  : expired.find(e => e === event)
                    ? false
                    : undefined,
                eventIdentifier: {
                  intendedStartDate: event.intendedStartDate.getTime(),
                  studyTemplateEventId:
                    event.participantEvent.studyTemplateEventId,
                  repeatCount: event.participantEvent.repeatCount || 0
                }
              };
            })
          });
        }
      }
      return {
        days: studyDuration,
        events: filterDiaryEvents(events)
      };
    }
  }, [participantEvents, studyDuration, studyParticipant, startDate]);

  const [showModal, setShowModal] = useState<boolean>();

  const mockData: DiaryEvent[] = [
    {
      events: [
        {
          day: 1,
          done: true
        },
        {
          day: 2,
          done: true
        },
        {
          day: 3,
          done: true
        },
        {
          day: 4,
          done: false
        },
        {
          day: 5,
          done: true
        },
        {
          day: 6,
          done: true
        },
        {
          day: 7,
          done: true
        },
        {
          day: 8,
          done: true
        },
        {
          day: 9,
          done: false
        },
        {
          day: 10,
          done: true
        },
        {
          day: 11,
          done: true
        },
        {
          day: 12,
          done: undefined
        },
        {
          day: 13,
          done: true
        },
        {
          day: 14,
          done: true
        },
        {
          day: 15,
          done: true
        },
        {
          day: 16,
          done: true
        },
        {
          day: 17,
          done: undefined
        },
        {
          day: 18,
          done: undefined
        }
      ],
      name: 'Daily Log Long Title'
    },
    {
      events: [
        {
          day: 1,
          done: true
        },
        {
          day: 7,
          done: true
        },
        {
          day: 14,
          done: undefined
        }
      ],
      name: 'Survey'
    },
    {
      events: [
        {
          day: 1,
          done: true
        },
        {
          day: 2,
          done: true
        },
        {
          day: 3,
          done: true
        },
        {
          day: 4,
          done: true
        },
        {
          day: 5,
          done: true
        },
        {
          day: 6,
          done: true
        },
        {
          day: 7,
          done: true
        },
        {
          day: 8,
          done: true
        },
        {
          day: 9,
          done: false
        },
        {
          day: 10,
          done: true
        },
        {
          day: 11,
          done: false
        },
        {
          day: 12,
          done: true
        },
        {
          day: 13,
          done: true
        },
        {
          day: 14,
          done: true
        },
        {
          day: 15,
          done: undefined
        },
        {
          day: 16,
          done: true
        },
        {
          day: 17,
          done: undefined
        },
        {
          day: 18,
          done: undefined
        }
      ],
      name: 'Product Use'
    }
  ];

  return (
    <StyledCard testID={elementIDs.pluginDataDiaryID}>
      <StyledTitleContainer>
        {chartTitle ? (
          <StyledH3
            testID={elementIDs.pluginDataDiaryTitleID}
            color='dark'
            padding={2}
          >
            {chartTitle}
          </StyledH3>
        ) : (
          <></>
        )}
        {hasMoreInfo && (
          <StyledIconButton
            onPress={() => setShowModal(true)}
            testID={elementIDs.pluginDataDiaryMoreInfoID}
          >
            <Octicons name='question' size={24} color='#494753' />
          </StyledIconButton>
        )}
      </StyledTitleContainer>
      <DataDiary
        currentDay={currentDay || 16}
        diaryEvents={diaryRecord?.events || mockData}
        studyLength={diaryRecord?.days || 18}
      />
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledCard>
  );
};

export const DataDiaryChartCardPlugin: ScreenComponentPlugin<DataDiaryCardProps> =
  {
    name: 'Data Diary Chart Card',
    type: 'DataDiaryChartCard',
    categories: ['study-progress'],
    component: DataDiaryChartCard
  };
